import CogSyncOutlineIcon from "mdi-react/CogSyncOutlineIcon";
import EyeOffOutline from "mdi-react/EyeOffOutlineIcon";
import EyeOutline from "mdi-react/EyeOutlineIcon";
import { useState, useRef, useEffect } from "react";
import {
  Modal,
  Form,
  Button,
  Row,
  Col,
  Image,
  InputGroup,
} from "react-bootstrap";
import { FaCamera, FaUpload, FaTimes } from "react-icons/fa";
import Select from "react-select";
import { nanoid } from "../../utils/helpers";
import { Formik, Form as FormikForm } from "formik";
import * as Yup from "yup";
import { backendApis } from "../../config";
import { useAuth } from "../../hooks/useAuth";
import { toast } from "react-toastify";
import ConfirmDialog from "../ConfirmDialogue";

const alLevels = ["", "High", "Low"];

const userType = ["advance", "core", "passive"];

// Validation schema for user account
const userAccountValidationSchema = (isEditMode) =>
  Yup.object().shape({
    Staff_ID: Yup.string().required("Staff ID is required"),
    Title: Yup.string(),
    Name: Yup.string().required("Full name is required"),
    username: Yup.string().required("Username is required"),
    password: isEditMode
      ? Yup.string()
      : Yup.string()
          .required("Password is required")
          .min(6, "Password must be at least 6 characters"),
    confirmpassword: isEditMode
      ? Yup.string()
      : Yup.string()
          .required("Confirm password is required")
          .oneOf([Yup.ref("password"), null], "Passwords must match"),
    Department: Yup.string().required("Department is required"),
    email: Yup.string()
      .email("Invalid email")
      .required("Email is required"),
  });

function DriverModal({
  showModal,
  setShowModal,
  formikProps,
  mutation,
  licenseClassOptions,
  statusOptions,
  isEditMode = false,
}) {
  const [profilePicture, setProfilePicture] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState(null);
  const [showText, setShowText] = useState({
    password: false,
    confirmpassword: false,
  });
  const { backendUrl, brachData } = useAuth();

  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Initial values for user account
  const initialUserAccountValues =
    // isEditMode && formikProps.values?.employee
    //   ? {
    //       Name: formikProps.values.employee.Name || "",
    //       password: "", // Don't prefill password for security
    //       confirmpassword: "",
    //       username: formikProps.values?.employee?.username || "",
    //       Department: formikProps.values?.employee?.Department || "",
    //       email: formikProps.values?.employee?.email || "",
    //       Staff_ID: formikProps.values?.employee?.Staff_ID || "",
    //       Title: formikProps.values?.employee?.Title || "",
    //       Branch: formikProps.values?.employee?.branch || "",
    //     }
    // :
    {
      Name: "",
      password: "",
      confirmpassword: "",
      username: "",
      contactPhone: "",
      Department: "Driver",
      email: "",
      Staff_ID: "",
      Title: "",
      Branch: [],
      brachData,
    };

  // Initialize preview URL when editing
  useEffect(() => {
    console.log({ driver: formikProps.values });
    if (isEditMode && formikProps.values?.picturePath) {
      setPreviewUrl(`${backendUrl}/static${formikProps.values.picturePath}`);
    } else if (!isEditMode) {
      setPreviewUrl(null);
      setProfilePicture(null);
    }
  }, [isEditMode, formikProps.values?.profilePictureUrl]);

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.warn("Please select an image file");
        return;
      }

      // Validate file size (e.g., 5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        toast.warn("File size must be less than 5MB");
        return;
      }

      setProfilePicture(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Start camera
  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: 640, height: 480 },
        audio: false,
      });

      setStream(mediaStream);
      setShowCamera(true);

      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      }, 100);
    } catch (err) {
      console.error("Error accessing camera:", err);
      toast.error(
        "Unable to access camera. Please check permissions or use file upload."
      );
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  };

  // Capture photo from camera
  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        (blob) => {
          const file = new File([blob], `driver-photo-${Date.now()}.jpg`, {
            type: "image/jpeg",
          });
          setProfilePicture(file);
          setPreviewUrl(canvas.toDataURL("image/jpeg"));
          stopCamera();
        },
        "image/jpeg",
        0.9
      );
    }
  };

  // Remove photo
  const removePhoto = () => {
    setProfilePicture(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle modal close
  const handleClose = () => {
    stopCamera();
    setShowModal(false);
    setProfilePicture(null);
    if (!isEditMode) {
      setPreviewUrl(null);
    }
    formikProps.resetForm();
  };

  return (
    <Modal show={showModal} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {isEditMode ? "Edit Driver" : "Add New Driver"}
        </Modal.Title>
      </Modal.Header>

      <Formik
        initialValues={initialUserAccountValues}
        // validationSchema={userAccountValidationSchema(isEditMode)}
        onSubmit={async (userAccountValues, { setSubmitting }) => {
          console.log("submitting data", userAccountValues, formikProps.values);
          // Create FormData to include the profile picture
          const formData = new FormData();

          // Append all driver data from the parent formik
          Object.keys(formikProps.values).forEach((key) => {
            if (
              formikProps.values[key] !== null &&
              formikProps.values[key] !== undefined &&
              key !== "profilePictureUrl"
            ) {
              formData.append(key, formikProps.values[key]);
            }
          });

          console.log(formikProps.values);

          // Append profile picture if a new one was uploaded
          if (profilePicture) {
            formData.append("profilePicture", profilePicture);
          }

          // Append user account data as JSON (excluding confirmpassword)
          const { confirmpassword, ...userAccountData } = userAccountValues;
          formData.append("userAccount", JSON.stringify(userAccountData));
          console.log(formData);
          await ConfirmDialog({
            title: isEditMode ? "Update Driver" : "Create new driver",
            description: isEditMode ? `Are you sure you want to update driver` : "Are you sure you want to create a new driver"
          })
          // Call the mutation directly for both add and edit
          mutation.mutate(formData, {
            onSuccess: () => {
              setSubmitting(false);
              setShowModal(false);
              formikProps.resetForm();
            },
            onError: (err) => {
              setSubmitting(false);
              console.log(err);
            },
          });
        }}
      >
        {({
          values: userAccountValues,
          errors: userAccountErrors,
          touched: userAccountTouched,
          handleChange: handleUserAccountChange,
          handleBlur: handleUserAccountBlur,
          handleSubmit: handleUserAccountSubmit,
          setFieldValue: setUserAccountFieldValue,
          isSubmitting,
        }) => (
          <FormikForm onSubmit={handleUserAccountSubmit}>
            <Modal.Body>
              {/* Profile Picture Section */}
              <div className="mb-4">
                <Form.Label className="fw-bold">Profile Picture</Form.Label>
                <div className="d-flex flex-column align-items-center">
                  {/* Preview */}
                  <div className="mb-3 position-relative">
                    {previewUrl ? (
                      <div className="position-relative">
                        <Image
                          src={previewUrl}
                          alt="Profile Preview"
                          roundedCircle
                          style={{
                            width: "150px",
                            height: "150px",
                            objectFit: "cover",
                            border: "3px solid #dee2e6",
                          }}
                        />
                        <Button
                          variant="danger"
                          size="sm"
                          className="position-absolute top-0 end-0"
                          style={{
                            borderRadius: "50%",
                            width: "30px",
                            height: "30px",
                            padding: 0,
                          }}
                          onClick={removePhoto}
                        >
                          <FaTimes />
                        </Button>
                      </div>
                    ) : (
                      <div
                        className="d-flex align-items-center justify-content-center bg-light rounded-circle"
                        style={{
                          width: "150px",
                          height: "150px",
                          border: "2px dashed #dee2e6",
                        }}
                      >
                        <FaCamera size={40} className="text-muted" />
                      </div>
                    )}
                  </div>

                  {/* Camera View */}
                  {showCamera && (
                    <div className="mb-3">
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        style={{
                          width: "100%",
                          maxWidth: "400px",
                          borderRadius: "8px",
                          border: "2px solid #dee2e6",
                        }}
                      />
                      <canvas ref={canvasRef} style={{ display: "none" }} />
                      <div className="d-flex gap-2 justify-content-center mt-2">
                        <Button variant="success" onClick={capturePhoto}>
                          Capture Photo
                        </Button>
                        <Button variant="secondary" onClick={stopCamera}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Upload Buttons */}
                  {!showCamera && (
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-primary"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <FaUpload className="me-2" />
                        {isEditMode && previewUrl
                          ? "Change Photo"
                          : "Select Photo"}
                      </Button>
                      <Button variant="outline-secondary" onClick={startCamera}>
                        <FaCamera className="me-2" />
                        Use Camera
                      </Button>
                    </div>
                  )}

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleFileUpload}
                  />
                  <small className="text-muted mt-2">
                    Accepted formats: JPG, PNG, GIF (Max 5MB)
                  </small>
                </div>
              </div>

              <hr className="my-4" />

              {/* Driver Information - Using parent formik */}
              <Row>
                {!isEditMode && (
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Employee ID <span className="text-danger">*</span>
                      </Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="text"
                          name="employee_id"
                          value={formikProps.values.employee_id || ""}
                          onChange={formikProps.handleChange}
                          onBlur={formikProps.handleBlur}
                          isInvalid={
                            formikProps.touched.employee_id &&
                            formikProps.errors.employee_id
                          }
                        />
                        <InputGroup.Text
                          title="Generate"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            formikProps.setFieldValue(
                              "employee_id",
                              nanoid(12, "number")
                            );
                          }}
                        >
                          <CogSyncOutlineIcon />
                        </InputGroup.Text>
                        <Form.Control.Feedback type="invalid">
                          {formikProps.errors.employee_id}
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>
                  </Col>
                )}
                {isEditMode && (
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Employee ID</Form.Label>
                      <Form.Control
                        type="text"
                        value={formikProps.values.employee_id || ""}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                )}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      License Number <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="licenseNumber"
                      value={formikProps.values.licenseNumber || ""}
                      onChange={formikProps.handleChange}
                      onBlur={formikProps.handleBlur}
                      isInvalid={
                        formikProps.touched.licenseNumber &&
                        formikProps.errors.licenseNumber
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {formikProps.errors.licenseNumber}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      License Class <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      name="licenseClass"
                      value={formikProps.values.licenseClass || ""}
                      onChange={formikProps.handleChange}
                      onBlur={formikProps.handleBlur}
                      isInvalid={
                        formikProps.touched.licenseClass &&
                        formikProps.errors.licenseClass
                      }
                    >
                      <option value="">Select License Class</option>
                      {licenseClassOptions.map((cls) => (
                        <option key={cls} value={cls}>
                          Class {cls}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {formikProps.errors.licenseClass}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      License Expiry <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="licenseExpiry"
                      value={formikProps.values.licenseExpiry || ""}
                      onChange={formikProps.handleChange}
                      onBlur={formikProps.handleBlur}
                      isInvalid={
                        formikProps.touched.licenseExpiry &&
                        formikProps.errors.licenseExpiry
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {formikProps.errors.licenseExpiry}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Medical Certificate Expiry</Form.Label>
                    <Form.Control
                      type="date"
                      name="medicalCertExpiry"
                      value={formikProps.values.medicalCertExpiry || ""}
                      onChange={formikProps.handleChange}
                      onBlur={formikProps.handleBlur}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Safety Score</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      name="safetyScore"
                      value={formikProps.values.safetyScore || ""}
                      onChange={formikProps.handleChange}
                      onBlur={formikProps.handleBlur}
                      placeholder="0-100"
                      isInvalid={
                        formikProps.touched.safetyScore &&
                        formikProps.errors.safetyScore
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {formikProps.errors.safetyScore}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Emergency Contact Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="emergencyContactName"
                      value={formikProps.values.emergencyContactName || ""}
                      onChange={formikProps.handleChange}
                      onBlur={formikProps.handleBlur}
                      placeholder="Full name"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Emergency Contact Phone</Form.Label>
                    <Form.Control
                      type="tel"
                      name="emergencyContactPhone"
                      value={formikProps.values.emergencyContactPhone || ""}
                      onChange={formikProps.handleChange}
                      onBlur={formikProps.handleBlur}
                      placeholder="Emergency contact phone"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Emergency Contact Relation</Form.Label>
                    <Form.Control
                      type="text"
                      name="emergencyContactRelation"
                      value={formikProps.values.emergencyContactRelation || ""}
                      onChange={formikProps.handleChange}
                      onBlur={formikProps.handleBlur}
                      placeholder="e.g., Spouse, Parent, Sibling"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      name="status"
                      value={formikProps.values.status || ""}
                      onChange={formikProps.handleChange}
                      onBlur={formikProps.handleBlur}
                    >
                      {statusOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Notes</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="notes"
                      value={formikProps.values.notes || ""}
                      onChange={formikProps.handleChange}
                      onBlur={formikProps.handleBlur}
                      placeholder="Additional notes about the driver..."
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* User Account Information - Using nested formik */}
              {!isEditMode && (
                <>
                  <hr className="my-4" />
                  <h5>User Account Information</h5>
                  <Row>
                    {/* <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Staff ID <span className="text-danger">*</span>
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="text"
                        name="Staff_ID"
                        value={userAccountValues.Staff_ID}
                        onChange={handleUserAccountChange}
                        onBlur={handleUserAccountBlur}
                        isInvalid={
                          userAccountTouched.Staff_ID &&
                          userAccountErrors.Staff_ID
                        }
                      />
                      <InputGroup.Text
                        title="Generate"
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setUserAccountFieldValue(
                            "Staff_ID",
                            nanoid(12, "number")
                          );
                        }}
                      >
                        <CogSyncOutlineIcon />
                      </InputGroup.Text>
                      <Form.Control.Feedback type="invalid">
                        {userAccountErrors.Staff_ID}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>
                </Col> */}
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Title</Form.Label>
                        <Form.Control
                          type="text"
                          name="Title"
                          value={userAccountValues.Title}
                          onChange={handleUserAccountChange}
                          onBlur={handleUserAccountBlur}
                          placeholder="e.g., Mr, Mrs"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Full Name <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="Name"
                          value={userAccountValues.Name}
                          onChange={handleUserAccountChange}
                          onBlur={handleUserAccountBlur}
                          isInvalid={
                            userAccountTouched.Name && userAccountErrors.Name
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {userAccountErrors.Name}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Username <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="username"
                          value={userAccountValues.username}
                          onChange={handleUserAccountChange}
                          onBlur={handleUserAccountBlur}
                          isInvalid={
                            userAccountTouched.username &&
                            userAccountErrors.username
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {userAccountErrors.username}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Contact Phone <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="tel"
                          name="contactPhone"
                          value={formikProps.values.contactPhone}
                          onChange={formikProps.handleChange}
                          onBlur={formikProps.handleBlur}
                          isInvalid={
                            formikProps.touched.contactPhone &&
                            formikProps.errors.contactPhone
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {userAccountErrors.contactPhone}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    {!isEditMode && (
                      <>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              Password <span className="text-danger">*</span>
                            </Form.Label>
                            <InputGroup>
                              <Form.Control
                                type={showText.password ? "text" : "password"}
                                name="password"
                                value={userAccountValues.password}
                                onChange={handleUserAccountChange}
                                onBlur={handleUserAccountBlur}
                                placeholder="********"
                                isInvalid={
                                  userAccountTouched.password &&
                                  userAccountErrors.password
                                }
                              />
                              <InputGroup.Text
                                style={{ cursor: "pointer" }}
                                onClick={() =>
                                  setShowText({
                                    ...showText,
                                    password: !showText.password,
                                  })
                                }
                              >
                                {showText.password ? (
                                  <EyeOutline />
                                ) : (
                                  <EyeOffOutline />
                                )}
                              </InputGroup.Text>
                              <Form.Control.Feedback type="invalid">
                                {userAccountErrors.password}
                              </Form.Control.Feedback>
                            </InputGroup>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              Confirm Password{" "}
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <InputGroup>
                              <Form.Control
                                type={
                                  showText.confirmpassword ? "text" : "password"
                                }
                                name="confirmpassword"
                                value={userAccountValues.confirmpassword}
                                onChange={handleUserAccountChange}
                                onBlur={handleUserAccountBlur}
                                placeholder="********"
                                isInvalid={
                                  userAccountTouched.confirmpassword &&
                                  userAccountErrors.confirmpassword
                                }
                              />
                              <InputGroup.Text
                                style={{ cursor: "pointer" }}
                                onClick={() =>
                                  setShowText({
                                    ...showText,
                                    confirmpassword: !showText.confirmpassword,
                                  })
                                }
                              >
                                {showText.confirmpassword ? (
                                  <EyeOutline />
                                ) : (
                                  <EyeOffOutline />
                                )}
                              </InputGroup.Text>
                              <Form.Control.Feedback type="invalid">
                                {userAccountErrors.confirmpassword}
                              </Form.Control.Feedback>
                            </InputGroup>
                          </Form.Group>
                        </Col>
                      </>
                    )}
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Branch <span className="text-danger">*</span>
                        </Form.Label>
                        <Select
                          menuPosition="fixed"
                          className="mx-3"
                          menuPlacement="auto"
                          placeholder="All"
                          name="Branch"
                          onChange={(selected, second) => {
                            console.log("branch selected: ", selected, second);
                            handleUserAccountChange({
                              target: {
                                name: "Branch",
                                value: selected,
                              },
                            });
                          }}
                          isSearchable={true}
                          isMulti
                          options={backendApis?.map((api) => ({
                            ...api,
                            value: api.name,
                            label: api.name,
                          }))}
                        />
                        <Form.Control.Feedback type="invalid">
                          {userAccountErrors.Department}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Email <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={userAccountValues.email}
                          onChange={(e) => {
                            console.log(e);
                            handleUserAccountChange(e);
                          }}
                          onBlur={handleUserAccountBlur}
                          isInvalid={
                            userAccountTouched.email && userAccountErrors.email
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {userAccountErrors.email}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Access Level <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          type="text"
                          name="AccessLavel"
                          value={userAccountValues.AccessLavel}
                          onChange={handleUserAccountChange}
                          onBlur={handleUserAccountBlur}
                          isInvalid={
                            userAccountTouched.AccessLavel &&
                            userAccountErrors.AccessLavel
                          }
                        >
                          {alLevels.map((a, i) => (
                            <option key={i} value={a}>
                              {a}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {userAccountErrors.AccessLavel}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          User Type <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          type="text"
                          name="userType"
                          value={userAccountValues.userType}
                          onChange={handleUserAccountChange}
                          onBlur={handleUserAccountBlur}
                          isInvalid={
                            userAccountTouched.userType &&
                            userAccountErrors.userType
                          }
                        >
                          {userType.map((a, i) => (
                            <option key={i} value={a}>
                              {a}
                            </option>
                          ))}
                        </Form.Select>

                        <Form.Control.Feedback type="invalid">
                          {userAccountErrors.userType}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                </>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={mutation.isLoading || isSubmitting}
              >
                {mutation.isLoading || isSubmitting
                  ? isEditMode
                    ? "Updating..."
                    : "Creating..."
                  : isEditMode
                  ? "Update Driver"
                  : "Create Driver"}
              </Button>
            </Modal.Footer>
          </FormikForm>
        )}
      </Formik>
    </Modal>
  );
}

export default DriverModal;
